/**
 * VistoeraTASK-00523 - clickable breadcrumb `全部图片 > 25 秋冬 > 女装 > 连衣裙`.
 * Deep paths collapse the middle into a "…" menu; every hidden level stays
 * reachable and the bar never widens the modal.
 */
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { RightOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { buildBreadcrumb, folderPath, type FolderLike } from './folderTreeModel'
import './image-folder-tree.css'

export interface ImageFolderBreadcrumbProps<T extends FolderLike> {
  folders: readonly T[]
  currentFolderId: string | null
  /** `null` = the root ("全部图片"). */
  onNavigate: (folderId: string | null) => void
  /** Label of the root crumb (defaults to 全部图片; the picker shows the active scope name). */
  rootLabel?: string
  testIdPrefix?: string
}

export default function ImageFolderBreadcrumb<T extends FolderLike>({
  folders,
  currentFolderId,
  onNavigate,
  rootLabel,
  testIdPrefix = 'ift-crumb',
}: ImageFolderBreadcrumbProps<T>) {
  const { t } = useTranslation('imageAssets')
  const path = folderPath(folders, currentFolderId)
  const segments = buildBreadcrumb(path)

  return (
    <nav className="ift-crumbs" aria-label={t('folders.breadcrumbAria')} data-testid={testIdPrefix}>
      {segments.map((seg, i) => {
        const sep = i > 0 ? <RightOutlined className="ift-crumb-sep" aria-hidden /> : null
        if (seg.kind === 'root') {
          const isCurrent = path.length === 0
          return (
            <span key="root" className="ift-crumb-item">
              <button
                type="button"
                className={`ift-crumb${isCurrent ? ' is-current' : ''}`}
                onClick={() => onNavigate(null)}
                aria-current={isCurrent ? 'page' : undefined}
                data-testid={`${testIdPrefix}-root`}
              >
                {rootLabel ?? t('folders.root')}
              </button>
            </span>
          )
        }
        if (seg.kind === 'ellipsis') {
          const items: MenuProps['items'] = seg.hidden.map((f) => ({
            key: f.id,
            label: f.name,
            onClick: () => onNavigate(f.id),
          }))
          return (
            <span key="ellipsis" className="ift-crumb-item">
              {sep}
              <Dropdown menu={{ items }} trigger={['click']}>
                <button
                  type="button"
                  className="ift-crumb"
                  aria-label={t('folders.breadcrumbMore')}
                  title={seg.hidden.map((f) => f.name).join(' / ')}
                  data-testid={`${testIdPrefix}-ellipsis`}
                >
                  …
                </button>
              </Dropdown>
            </span>
          )
        }
        return (
          <span key={seg.folder.id} className="ift-crumb-item">
            {sep}
            <button
              type="button"
              className={`ift-crumb${seg.isCurrent ? ' is-current' : ''}`}
              onClick={() => onNavigate(seg.folder.id)}
              aria-current={seg.isCurrent ? 'page' : undefined}
              title={seg.folder.name}
              data-testid={`${testIdPrefix}-folder`}
              data-folder-id={seg.folder.id}
            >
              {seg.folder.name}
            </button>
          </span>
        )
      })}
    </nav>
  )
}
